import { nanoid } from 'nanoid';
import type { FormField, FormFieldTypeId } from '@/types/form-definition';

const LABELS: Partial<Record<FormFieldTypeId, string>> = {
  shortText: 'Short text',
  longText: 'Long text',
  email: 'Email',
  phone: 'Phone',
  number: 'Number',
  dropdown: 'Dropdown',
  singleChoice: 'Choose one',
  multipleChoice: 'Choose any',
  datePicker: 'Date',
  time: 'Time',
  header: 'Section heading',
  paragraph: 'Description text',
  richText: 'Rich text',
  divider: 'Divider',
  pageBreak: 'Page break',
  submit: 'Submit',
  appointment: 'Book appointment',
  payment: 'Payment',
  fullName: 'Full name',
  address: 'Address',
  signature: 'Signature',
  fileUpload: 'File upload',
};

function asPlainObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function withQuestionProperties(
  field: FormField,
  opts?: { isLayout?: boolean; requiredDefault?: boolean }
): FormField {
  const qp = asPlainObject(field.questionProperties) ?? {};
  const general = asPlainObject(qp.general) ?? {};
  const advanced = asPlainObject(qp.advanced) ?? {};

  const isLayout = Boolean(opts?.isLayout);
  const requiredDefault = Boolean(opts?.requiredDefault);

  const nextGeneral: Record<string, unknown> = {
    ...(isLayout ? {} : { required: requiredDefault, labelAlign: 'top' }),
    labelVisible: true,
    ...general,
  };

  return {
    ...field,
    questionProperties: {
      ...qp,
      general: nextGeneral,
      advanced,
    },
  };
}

function withConfig(field: FormField, config: Record<string, unknown> | undefined): FormField {
  const existing = asPlainObject(field.config);
  if (existing) return field;
  if (!config || Object.keys(config).length === 0) return field;
  return { ...field, config };
}

function withValidation(field: FormField, validation: Record<string, unknown> | undefined): FormField {
  const existing = asPlainObject(field.validation);
  if (existing) return field;
  if (!validation || Object.keys(validation).length === 0) return field;
  return { ...field, validation };
}

export function normalizeField(field: FormField): FormField {
  const type = field.type as FormFieldTypeId;

  // Layout blocks
  if (type === 'header') {
    const base = withQuestionProperties({ ...field, label: field.label ?? LABELS.header ?? 'Heading' }, { isLayout: true });
    return withConfig(base, { subHeader: undefined });
  }

  if (type === 'paragraph') {
    const base = withQuestionProperties(
      {
        ...field,
        html: typeof field.html === 'string' && field.html.trim().length > 0 ? field.html : '<p>Enter paragraph text</p>',
      },
      { isLayout: true }
    );
    return base;
  }

  if (type === 'richText') {
    const base = withQuestionProperties(
      {
        ...field,
        label: field.label ?? (LABELS.richText ?? 'Rich text'),
        html:
          typeof field.html === 'string' && field.html.trim().length > 0
            ? field.html
            : '<h3>Terms &amp; Conditions</h3><p>Add rich formatted content here.</p>',
      },
      { isLayout: true }
    );
    return base;
  }

  if (type === 'divider') {
    const base = withQuestionProperties(field, { isLayout: true });
    return withConfig(base, { style: 'line' });
  }

  if (type === 'pageBreak') {
    const base = withQuestionProperties(field, { isLayout: true });
    return withConfig(base, { pageTitle: undefined, pageDescription: undefined });
  }

  if (type === 'submit') {
    const base = withQuestionProperties({ ...field, label: field.label ?? 'Submit' }, { isLayout: true });
    return withConfig(base, { buttonVariant: 'primary', fullWidth: true });
  }

  // Choice fields
  if (type === 'dropdown' || type === 'singleChoice' || type === 'multipleChoice') {
    const options = Array.isArray(field.config?.options)
      ? undefined
      : [
          { value: 'Option 1', label: 'Option 1' },
          { value: 'Option 2', label: 'Option 2' },
        ];

    const displayAs =
      type === 'dropdown' ? undefined : type === 'singleChoice' ? 'radio' : 'checkbox';

    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS[type] ?? type) });
    const withCfg = withConfig(base, {
      options,
      allowOther: false,
      shuffleOptions: false,
      ...(type === 'dropdown' ? { placeholderOption: 'Select…' } : { displayAs }),
    });

    // Ensure allowedValues exists when we inserted options.
    if (options) {
      return withValidation(withCfg, { allowedValues: options.map((o) => o.value) });
    }
    return withCfg;
  }

  // Input fields
  if (type === 'longText') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.longText ?? type) });
    return withConfig(base, { rows: 4, richText: false });
  }

  if (type === 'number') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.number ?? type) });
    return withConfig(base, { useGrouping: true, decimalPlaces: 0 });
  }

  if (type === 'time') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.time ?? type) });
    return withConfig(base, { format: '12h', minuteStep: 5 });
  }

  if (type === 'datePicker') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.datePicker ?? type) });
    return withConfig(base, { includeTime: false, dateFormat: 'YYYY-MM-DD', useNativePicker: true });
  }

  if (type === 'email') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.email ?? type) });
    return withConfig(base, { confirmEmail: false, sendVerification: false });
  }

  if (type === 'phone') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.phone ?? type) });
    return withConfig(base, { defaultCountryCode: 'US', allowExtension: false });
  }

  if (type === 'fullName') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.fullName ?? type) });
    return withConfig(base, {
      showPrefix: false,
      showMiddle: false,
      showSuffix: false,
      labels: { first: 'First name', last: 'Last name' },
      requireSubfields: {},
    });
  }

  if (type === 'address') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.address ?? type) });
    return withConfig(base, {
      layout: 'us',
      subfieldIds: ['street1', 'street2', 'city', 'state', 'zip', 'country'],
      requireSubfields: {},
    });
  }

  if (type === 'signature') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.signature ?? type) });
    return withConfig(base, { canvasWidth: 600, canvasHeight: 200, penColor: '#111827', legalDisclaimer: undefined });
  }

  if (type === 'fileUpload') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.fileUpload ?? type) });
    const withCfg = withConfig(base, { multiple: false, dragDrop: true });
    return withValidation(withCfg, {
      maxFiles: 1,
      maxFileSizeBytes: 10 * 1024 * 1024,
    });
  }

  if (type === 'appointment') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.appointment ?? type) });
    return withConfig(base, {
      scheduleTimeZone: 'America/New_York',
      slotDurationMinutes: 30,
      slotIntervalMinutes: 30,
      bufferBeforeMinutes: 0,
      bufferAfterMinutes: 0,
      weeklySchedule: {
        sun: { enabled: false, ranges: [] },
        mon: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        tue: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        wed: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        thu: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        fri: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        sat: { enabled: false, ranges: [] },
      },
      capacity: { mode: 'one_on_one', maxAttendeesPerSlot: 1 },
    });
  }

  if (type === 'payment') {
    const base = withQuestionProperties({ ...field, label: field.label ?? (LABELS.payment ?? type) });
    return withConfig(base, {
      gateway: 'square',
      amountSource: 'fixed',
      amount: 0,
      currency: 'USD',
    });
  }

  // shortText (and any other supported input types without special config)
  return withQuestionProperties({ ...field, label: field.label ?? (LABELS[type] ?? type) });
}

export function createDefaultField(type: FormFieldTypeId): FormField {
  const id = nanoid(12);
  const label = LABELS[type] ?? type;

  if (type === 'header') {
    return normalizeField({ id, type, label });
  }

  if (type === 'paragraph') {
    return normalizeField({ id, type, html: '<p>Enter paragraph text</p>' });
  }

  if (type === 'richText') {
    return normalizeField({ id, type, label, html: '<h3>Rich content</h3><p>Start writing…</p>' });
  }

  if (type === 'divider' || type === 'pageBreak') {
    return normalizeField({ id, type });
  }

  if (type === 'submit') {
    return normalizeField({ id, type, label: 'Submit' });
  }

  if (type === 'dropdown' || type === 'singleChoice' || type === 'multipleChoice') {
    return normalizeField({ id, type, label });
  }

  return normalizeField({ id, type, label });
}

export function emptyDefinition(): FormField[] {
  return [];
}
